/**
 * Single source for currency display metadata (code, name, symbol, flag).
 * Use getCurrencyInfo(code) for safe lookup; unknown codes get a fallback.
 */

import { z } from "zod/v4";

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
  flag: string;
}

export const CURRENCY_INFO: Record<string, CurrencyInfo> = {
  GBP: {
    code: "GBP",
    name: "British Pound",
    symbol: "£",
    flag: "🇬🇧",
  },
  EUR: {
    code: "EUR",
    name: "Euro",
    symbol: "€",
    flag: "🇪🇺",
  },
  USD: {
    code: "USD",
    name: "US Dollar",
    symbol: "$",
    flag: "🇺🇸",
  },
  NGN: {
    code: "NGN",
    name: "Nigerian Naira",
    symbol: "₦",
    flag: "🇳🇬",
  },
  KES: {
    code: "KES",
    name: "Kenyan Shilling",
    symbol: "KSh",
    flag: "🇰🇪",
  },
  GHS: {
    code: "GHS",
    name: "Ghanaian Cedi",
    symbol: "₵",
    flag: "🇬🇭",
  },
  ZAR: {
    code: "ZAR",
    name: "South African Rand",
    symbol: "R",
    flag: "🇿🇦",
  },
  UGX: {
    code: "UGX",
    name: "Ugandan Shilling",
    symbol: "USh",
    flag: "🇺🇬",
  },
  TZS: {
    code: "TZS",
    name: "Tanzanian Shilling",
    symbol: "TSh",
    flag: "🇹🇿",
  },
  PKR: {
    code: "PKR",
    name: "Pakistani Rupee",
    symbol: "₨",
    flag: "🇵🇰",
  },
  XAF: {
    code: "XAF",
    name: "Central African CFA franc",
    symbol: "Fr",
    flag: "🇨🇲",
  },
  XOF: {
    code: "XOF",
    name: "West African CFA franc",
    symbol: "Fr",
    flag: "🇸🇳",
  },
  OMR: {
    code: "OMR",
    name: "Omani Rial",
    symbol: "OMR",
    flag: "🇴🇲",
  },
  AED: {
    code: "AED",
    name: "UAE Dirham",
    symbol: "AED",
    flag: "🇦🇪",
  },
  USDC: {
    code: "USDC",
    name: "USD Coin",
    symbol: "USDC",
    flag: "🪙",
  },
  USDT: {
    code: "USDT",
    name: "Tether",
    symbol: "USDT",
    flag: "🪙",
  },
} as const;

/**
 * A currency or asset code as the FX backend accepts it: ISO 4217 codes and stablecoins
 * (USDT, USDC), 3 to 8 capitals.
 */
export const FxCurrencyCodeSchema = z.string().regex(/^[A-Z]{3,8}$/);

/** Stablecoins the backend settles in six decimals; every other currency shows two. */
const SIX_DECIMAL_CURRENCIES: readonly string[] = ["USDT", "USDC"];
export const amountDecimals = (currency: string) =>
  SIX_DECIMAL_CURRENCIES.includes(currency.toUpperCase()) ? 6 : 2;

const amountFormatters = new Map<number, Intl.NumberFormat>();

/** A major-unit amount at its currency's precision: `1,000.00` GBP, `12.345678` USDT. */
export function formatCurrencyAmount(amount: number, currency: string): string {
  const decimals = amountDecimals(currency);
  let formatter = amountFormatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    amountFormatters.set(decimals, formatter);
  }
  return formatter.format(amount);
}

/** Networks are stored as entered ("solana", "TRC20"); show lower-case names capitalised. */
export const walletNetworkLabel = (network: string) =>
  network === network.toLowerCase()
    ? network.charAt(0).toUpperCase() + network.slice(1)
    : network;

export function getCurrencyInfo(code: string): CurrencyInfo {
  const upper = code.toUpperCase();
  const known = CURRENCY_INFO[upper];
  if (known) return known;
  return {
    code: upper,
    name: upper,
    symbol: upper,
    flag: "💱",
  };
}

/** Currency symbol for formatting amounts (re-export for callers that only need symbol). */
export function getCurrencySymbol(code: string): string {
  return getCurrencyInfo(code).symbol;
}

/**
 * Format an amount with its currency symbol. Single-character symbols render
 * prefix style ("€1.2704"); multi-letter code symbols (USDC, USDT, OMR, AED)
 * render suffix style ("1.2704 USDC") instead of the unreadable "USDC1.2704".
 */
export function formatAmountWithSymbol(
  code: string,
  formattedAmount: string,
): string {
  const symbol = getCurrencySymbol(code);
  return symbol.length === 1
    ? `${symbol}${formattedAmount}`
    : `${formattedAmount} ${symbol}`;
}
